export interface AppDomRefs {
  appShell: HTMLElement;
  gameLayout: HTMLElement;
  playSurface: HTMLElement;
  boardCanvas: HTMLCanvasElement;
  loadingOverlay: HTMLElement;
  loadingCopy: HTMLElement;
  nextCanvas: HTMLCanvasElement;
  scoreValue: HTMLElement;
  levelValue: HTMLElement;
  chainValue: HTMLElement;
  typeValue: HTMLElement;
  levelProgress: HTMLElement;
  pauseButton: HTMLButtonElement;
  rankingButton: HTMLButtonElement;
  achievementsButton: HTMLButtonElement;
  rankingList: HTMLOListElement;
  touchControls: HTMLElement;
  centerPrompt: HTMLElement;
  statusToast: HTMLElement;
  screenCurtain: HTMLElement;
  modal: HTMLDialogElement;
  modalTitle: HTMLElement;
  modalBody: HTMLElement;
  nameInput: HTMLInputElement;
  nameWarning: HTMLElement;
  nameForm: HTMLFormElement;
  modalExtraAction: HTMLButtonElement;
  modalCancel: HTMLButtonElement;
  modalConfirm: HTMLButtonElement;
}

export function mountApp(root: HTMLElement): AppDomRefs {
  root.innerHTML = `
    <div class="app-shell is-loading">
      <header class="topbar">
        <div class="brand">
          <span class="brand-mark" aria-hidden="true"><img src="/icons/icon-192.png" alt="" /></span>
          <div>
            <h1>Friel</h1>
          </div>
        </div>
        <nav class="top-actions" aria-label="Game actions" data-i18n-aria="gameActions">
          <button id="startButton" class="command-button primary" type="button" data-i18n="startButton">Start</button>
          <button id="pauseButton" class="icon-button pause-button" type="button" title="Pause" aria-label="Pause" hidden>&#10074;&#10074;</button>
          <button id="rankingButton" class="icon-button" type="button" title="Ranking" aria-label="Ranking" data-i18n-title="ranking" data-i18n-aria="ranking">#</button>
          <button id="achievementsButton" class="icon-button" type="button" title="Achievements" aria-label="Achievements" data-i18n-title="achievements" data-i18n-aria="achievements">&#9733;</button>
          <button id="helpButton" class="icon-button" type="button" title="Help" aria-label="Help" data-i18n-title="help" data-i18n-aria="help">?</button>
          <button id="settingsButton" class="icon-button" type="button" title="Settings" aria-label="Settings" data-i18n-title="settings" data-i18n-aria="settings">&#9881;</button>
        </nav>
      </header>

      <main id="gameLayout" class="game-layout">
        <section id="playSurface" class="play-surface" aria-label="Friel board" data-i18n-aria="board">
          <canvas id="boardCanvas"></canvas>
          <div id="loadingOverlay" class="loading-overlay" role="status" aria-live="polite">
            <span class="loading-spinner" aria-hidden="true"></span>
            <span id="loadingCopy">Loading...</span>
          </div>
          <div id="screenCurtain" class="screen-curtain" aria-hidden="true"></div>
          <div id="centerPrompt" class="center-prompt">
            <b class="logo-word" aria-label="FRIEL"><span>f</span><span>r</span><span>i</span><span>e</span><span>l</span></b>
            <span class="prompt-action">START</span>
          </div>
          <div id="statusToast" class="status-toast" aria-live="polite"></div>
        </section>

        <aside class="side-panel" aria-label="Game status" data-i18n-aria="status">
          <div class="title-lockup" aria-hidden="true">
            <span class="friel-logo">
              <b>f</b><b>r</b><b>i</b><b>e</b><b>l</b>
            </span>
          </div>
          <div class="meter-strip primary-meter">
            <div class="metric score-metric">
              <span data-i18n="score">Score</span>
              <strong id="scoreValue">0</strong>
            </div>
            <div class="metric level-metric">
              <span data-i18n="level">Level</span>
              <strong id="levelValue">1</strong>
            </div>
          </div>
          <div class="meter-strip compact secondary-meter">
            <div class="metric chain-metric">
              <span data-i18n="chain">Chain</span>
              <strong id="chainValue">0</strong>
            </div>
            <div class="metric type-metric">
              <span data-i18n="types">Types</span>
              <strong id="typeValue">4</strong>
            </div>
          </div>
          <div class="progress-row">
            <span data-i18n="next">Next</span>
            <div class="progress-track"><span id="levelProgress"></span></div>
          </div>
          <div class="next-panel">
            <span data-i18n="nextPair">Next Pair</span>
            <canvas id="nextCanvas" width="96" height="160"></canvas>
          </div>
          <div class="ranking-panel">
            <div class="panel-heading">
              <span data-i18n="ranking">Ranking</span>
              <small data-i18n="topTen">Top 10</small>
            </div>
            <ol id="rankingList"></ol>
          </div>
        </aside>
      </main>

      <div id="touchControls" class="touch-controls" aria-label="Touch controls" data-i18n-aria="touchControls">
        <button data-command="left" class="pad-button" type="button" aria-label="Left" data-i18n-aria="left" data-key-hint="Arrow Left / A">&larr;</button>
        <button data-command="right" class="pad-button" type="button" aria-label="Right" data-i18n-aria="right" data-key-hint="Arrow Right / D">&rarr;</button>
        <button data-command="rotateLeft" class="pad-button rotate" type="button" aria-label="Rotate left" data-i18n-aria="rotateLeft" data-key-hint="Q / Z">&#8634;</button>
        <button data-command="rotateRight" class="pad-button rotate" type="button" aria-label="Rotate right" data-i18n-aria="rotateRight" data-key-hint="E / W / X / Up">&#8635;</button>
        <button data-command="softDrop" class="pad-button drop" type="button" aria-label="Soft drop" data-i18n-aria="softDrop" data-key-hint="Arrow Down / S">&darr;</button>
        <button data-command="hardDrop" class="pad-button hard-drop" type="button" aria-label="Hard drop" data-i18n-aria="hardDrop" data-key-hint="Space">&#10515;</button>
      </div>

      <footer class="copyright-footer">&copy; <a href="https://www.frieve.com/" target="_blank" rel="noopener noreferrer">Frieve</a> 2026</footer>

      <dialog id="modal">
        <form id="nameForm" method="dialog" class="modal-panel">
          <h2 id="modalTitle">Name Entry</h2>
          <div id="modalBody" class="modal-body"></div>
          <input id="nameInput" name="name" autocomplete="off" spellcheck="false" aria-describedby="nameWarning" />
          <p id="nameWarning" class="name-warning" aria-live="polite" hidden></p>
          <div class="modal-actions">
            <button id="modalExtraAction" class="text-button danger-button modal-extra-action" type="button" hidden></button>
            <button id="modalCancel" class="text-button" value="cancel" type="button" data-i18n="close">Close</button>
            <button id="modalConfirm" class="command-button primary" value="default" type="submit" data-i18n="ok">OK</button>
          </div>
        </form>
      </dialog>

      <template id="settingsTemplate">
        <div class="settings-panel">
          <label class="settings-row audio-row">
            <span><input id="bgmToggle" type="checkbox" /> <span data-i18n="bgm">BGM</span></span>
            <input id="bgmVolume" type="range" min="0" max="100" step="10" />
            <output id="bgmVolumeValue">70</output>
          </label>
          <label class="settings-row audio-row">
            <span><input id="seToggle" type="checkbox" /> <span data-i18n="se">SE</span></span>
            <input id="seVolume" type="range" min="0" max="100" step="10" />
            <output id="seVolumeValue">70</output>
          </label>
          <label class="settings-row">
            <span><input id="touchToggle" type="checkbox" /> <span data-i18n="touchControlsSetting">Touch Controls</span></span>
          </label>
          <label class="settings-row language-row">
            <span data-i18n="language">Language</span>
            <select id="languageSelect"></select>
          </label>
          <button id="pwaInstallButton" class="command-button primary settings-install-button" type="button" hidden data-i18n="installApp">Install app</button>
        </div>
      </template>
    </div>
  `;

  return {
    appShell: get(".app-shell", HTMLElement),
    gameLayout: get("#gameLayout", HTMLElement),
    playSurface: get("#playSurface", HTMLElement),
    boardCanvas: get("#boardCanvas", HTMLCanvasElement),
    loadingOverlay: get("#loadingOverlay", HTMLElement),
    loadingCopy: get("#loadingCopy", HTMLElement),
    nextCanvas: get("#nextCanvas", HTMLCanvasElement),
    scoreValue: get("#scoreValue", HTMLElement),
    levelValue: get("#levelValue", HTMLElement),
    chainValue: get("#chainValue", HTMLElement),
    typeValue: get("#typeValue", HTMLElement),
    levelProgress: get("#levelProgress", HTMLElement),
    pauseButton: get("#pauseButton", HTMLButtonElement),
    rankingButton: get("#rankingButton", HTMLButtonElement),
    achievementsButton: get("#achievementsButton", HTMLButtonElement),
    rankingList: get("#rankingList", HTMLOListElement),
    touchControls: get("#touchControls", HTMLElement),
    centerPrompt: get("#centerPrompt", HTMLElement),
    statusToast: get("#statusToast", HTMLElement),
    screenCurtain: get("#screenCurtain", HTMLElement),
    modal: get("#modal", HTMLDialogElement),
    modalTitle: get("#modalTitle", HTMLElement),
    modalBody: get("#modalBody", HTMLElement),
    nameInput: get("#nameInput", HTMLInputElement),
    nameWarning: get("#nameWarning", HTMLElement),
    nameForm: get("#nameForm", HTMLFormElement),
    modalExtraAction: get("#modalExtraAction", HTMLButtonElement),
    modalCancel: get("#modalCancel", HTMLButtonElement),
    modalConfirm: get("#modalConfirm", HTMLButtonElement)
  };
}

function get<T extends Element>(selector: string, ctor: { new (): T }): T {
  const element = document.querySelector(selector);
  if (!(element instanceof ctor)) {
    throw new Error(`${selector} was not found`);
  }
  return element;
}
